// -----------------------------------------------------------
// FloraIQ - CadastralActionsSection (Aba Ações de Cadastramento)
// -----------------------------------------------------------

import { useState } from "react";
import { cadastralActions } from "@/mocks/operational";
import type { CadastralAction } from "@/mocks/operational";
import StageStatusBadge from "./StageStatusBadge";

interface CadastralActionsSectionProps {
  onViewAction: (action: CadastralAction) => void;
}

const statusLabels: Record<string, { label: string; color: string }> = {
  planejada: { label: "Planejada", color: "bg-blue-100 text-blue-700" },
  em_andamento: { label: "Em andamento", color: "bg-accent-100 text-accent-700" },
  em_validacao: { label: "Em validação", color: "bg-yellow-100 text-yellow-700" },
  concluida: { label: "Concluída", color: "bg-green-100 text-green-700" },
  bloqueada: { label: "Bloqueada", color: "bg-red-100 text-red-700" },
  suspensa: { label: "Suspensa", color: "bg-background-200 text-foreground-600" },
};

export default function CadastralActionsSection({ onViewAction }: CadastralActionsSectionProps) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");

  const filtered = cadastralActions.filter((a) => {
    if (search && !a.nome.toLowerCase().includes(search.toLowerCase()) && !a.municipio.toLowerCase().includes(search.toLowerCase()) && !a.responsavel.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter && a.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por nome, município ou responsável..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="planejada">Planejada</option>
          <option value="em_andamento">Em andamento</option>
          <option value="em_validacao">Em validação</option>
          <option value="concluida">Concluída</option>
          <option value="bloqueada">Bloqueada</option>
          <option value="suspensa">Suspensa</option>
        </select>
        <div className="flex rounded-lg border border-background-200/70 overflow-hidden">
          <button onClick={() => setViewMode("table")} className={`px-3 py-2 text-xs cursor-pointer transition-colors whitespace-nowrap ${viewMode === "table" ? "bg-primary-500 text-white" : "bg-white text-foreground-600 hover:bg-background-50"}`}>
            <i className="ri-list-check mr-1"></i>Tabela
          </button>
          <button onClick={() => setViewMode("cards")} className={`px-3 py-2 text-xs cursor-pointer transition-colors whitespace-nowrap ${viewMode === "cards" ? "bg-primary-500 text-white" : "bg-white text-foreground-600 hover:bg-background-50"}`}>
            <i className="ri-layout-grid-line mr-1"></i>Cards
          </button>
        </div>
      </div>

      {/* Table View */}
      {viewMode === "table" && (
        <div className="overflow-x-auto rounded-card border border-background-200/70">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/50 bg-background-50/70">
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500">Ação</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500">Município/UF</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500">Etapa Atual</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500">Progresso</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500">Árvores</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500">Responsável</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500">Status</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const st = statusLabels[a.status];
                return (
                  <tr key={a.id} className="border-b border-background-100/70 hover:bg-background-50/50 transition-colors">
                    <td className="px-3 py-2.5">
                      <p className="text-xs font-medium text-foreground-800">{a.nome}</p>
                      <p className="text-[10px] text-foreground-400">{a.projetoVinculado}</p>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-foreground-600">{a.municipio}/{a.uf}</td>
                    <td className="px-3 py-2.5 text-xs text-foreground-600">{a.etapaAtual}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-background-100 rounded-full h-1.5">
                          <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: `${a.progresso}%` }}></div>
                        </div>
                        <span className="text-xs font-bold text-foreground-800">{a.progresso}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-foreground-600">{a.arvoresCadastradas}/{a.arvoresPrevistas}</td>
                    <td className="px-3 py-2.5 text-xs text-foreground-600">{a.responsavel}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${st?.color || ""}`}>
                        {st?.label || a.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <button onClick={() => onViewAction(a)} className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                        <i className="ri-eye-line mr-1"></i>Abrir fluxo
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-xs text-foreground-400">
                    Nenhuma ação encontrada com os filtros atuais
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Card View */}
      {viewMode === "cards" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((a) => {
            const st = statusLabels[a.status];
            return (
              <div key={a.id} className="rounded-card border border-background-200/70 p-4 hover:border-primary-200 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h5 className="text-sm font-bold text-foreground-900 truncate">{a.nome}</h5>
                    <p className="text-[10px] text-foreground-400">{a.municipio}/{a.uf} • {a.areaEstudo}</p>
                  </div>
                  <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap shrink-0 ml-2 ${st?.color || ""}`}>
                    {st?.label || a.status}
                  </span>
                </div>
                <div className="space-y-1.5 mb-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-500">Etapa atual</span>
                    <span className="text-foreground-700 font-medium">{a.etapaAtual}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-500">Árvores</span>
                    <span className="text-foreground-700 font-medium">{a.arvoresCadastradas}/{a.arvoresPrevistas}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-500">Pendências</span>
                    <span className={`font-medium ${a.pendencias > 3 ? "text-red-600" : "text-foreground-700"}`}>{a.pendencias}</span>
                  </div>
                  <div className="w-full bg-background-100 rounded-full h-1.5 mt-1">
                    <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: `${a.progresso}%` }}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex -space-x-1.5">
                    {a.equipe.slice(0, 3).map((nome, i) => (
                      <div key={i} className="w-6 h-6 rounded-full bg-accent-100 border-2 border-white flex items-center justify-center" title={nome}>
                        <span className="text-[9px] font-bold text-accent-600">{nome.charAt(0)}</span>
                      </div>
                    ))}
                    {a.equipe.length > 3 && (
                      <div className="w-6 h-6 rounded-full bg-background-200 border-2 border-white flex items-center justify-center">
                        <span className="text-[9px] text-foreground-500">+{a.equipe.length - 3}</span>
                      </div>
                    )}
                  </div>
                  <button onClick={() => onViewAction(a)} className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                    Abrir fluxo<i className="ri-arrow-right-line ml-1"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-foreground-400">
        Mostrando {filtered.length} de {cadastralActions.length} ações
      </p>
    </div>
  );
}